<template>
  <div class="app-container">
    <header class="header">
      <div class="header-content">
        <!-- Back Button -->
        <button class="icon-button back-btn" @click="navigateToDashboard">
          <i class="fa-solid fa-arrow-left"></i>
          <span class="tooltip-text">Back</span>
        </button>

        <!-- Title -->
        <h1 class="title">Real-Time GPS Tracker</h1>

        <!-- Connection Status -->
        <div class="connection-status" :class="connectionStatusClass">
          <div class="status-dot"></div>
          <span>{{ connectionStatusText }}</span>
        </div>

        <!-- Full Screen Button -->
        <button class="icon-button expand-btn" @click="fullScreen">
          <i :class="isFullScreen ? 'fa-solid fa-compress' : 'fa-solid fa-expand'"></i>
          <span class="tooltip-text">{{ isFullScreen ? 'Exit Fullscreen' : 'Full Screen' }}</span>
        </button>

        <!-- Activity Button -->
        <button class="icon-button activity-btn" @click="activityLog">
          <i class="fa-solid fa-chart-simple"></i>
          <span class="tooltip-text">Active Users Summary</span>
        </button>

        <!-- Filter Button -->
        <button class="icon-button filter-btn" ref="filterBtn" @click="filteringMap">
          <i class="fa-solid fa-filter"></i>
          <span class="tooltip-text">Filters</span>
        </button>

        <!-- Log Button -->
        <button class="icon-button log-btn" @click="toggleLogSheet">
          <i class="fa-solid fa-terminal"></i>
          <span class="tooltip-text">View Logs</span>
        </button>

        <!-- History Button -->
        <button class="icon-button history-btn" @click="showLocationHistory">
          <i class="fa-solid fa-clock-rotate-left"></i>
          <span class="tooltip-text">History</span>
        </button>

        <!-- Menu Button -->
        <button class="icon-button menu-btn" @click="toggleMenu">
          <i class="fa-solid fa-ellipsis-vertical"></i>
          <span class="tooltip-text">Menu</span>
        </button>
      </div>
    </header>

    <!-- Map Container -->
    <main class="main-content">
      <div id="map" class="map-container"></div>
    </main>

    <!-- Right Side Menu for Tracking Schedule -->
    <div class="overlay-menu" :class="{ open: isMenuOpen }" @click.self="toggleMenu">
      <div class="menu-content">
        <div class="menu-header">
          <h2>Enable Location Tracking</h2>
          <button class="icon-button close-btn" @click="toggleMenu">
            <i class="fa-solid fa-xmark"></i>
            <span class="tooltip-text">Close</span>
          </button>
        </div>

        <div class="menu-body">
          <div v-if="loading" class="loading-overlay">
            <div class="location-loader">
              <i class="fa-solid fa-location-dot"></i>
              <span class="pulse"></span>
            </div>
          </div>

          <div v-if="!loading && Object.keys(trackingSchedule).length > 0">
            <div v-for="(users, role) in trackingSchedule" :key="role" class="role-card">

              <div class="role-header">
                <h3>{{ role }}</h3>
                <button class="icon-button" @click="toggleRoleExpand(role)">
                  <i :class="expandedRoles.includes(role) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
                </button>
              </div>

              <div v-show="expandedRoles.includes(role)">

                <div v-for="user in users" :key="user.id" class="user-card">

                  <div class="user-info-row">
                    <span class="user-name">{{ user.name }}</span>

                    <label class="switch">
                      <input type="checkbox" v-model="user.trackingEnabled" @change="onTrackingEnabledChange(user)" />
                      <span class="slider round"></span>
                    </label>
                  </div>

                  <div v-if="user.trackingEnabled" class="tracking-settings">

                    <div
                        class="day-schedule"
                        v-for="day in daysOfWeek"
                        :key="day"
                    >

                      <div class="day-checkbox">
                        <input type="checkbox" v-model="user.schedule[day].enabled" />
                      </div>

                      <div class="day-label">
                        {{ day.slice(0, 3) }}
                      </div>

                      <div>
                        <input
                            type="time"
                            v-model="user.schedule[day].start"
                            :disabled="!user.schedule[day].enabled"
                        />
                      </div>

                      <div>
                        <input
                            type="time"
                            v-model="user.schedule[day].end"
                            :disabled="!user.schedule[day].enabled"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!loading && Object.keys(trackingSchedule).length === 0" class="not-found-widget">
            <img src="@/assets/vectors/data_not_found.png" alt="No Promotions" class="error-image" />
            <p class="text-center error-message">Users Not Found!</p>
          </div>
        </div>

        <div v-if="!loading && Object.keys(trackingSchedule).length > 0" class="menu-footer">
          <button class="save-btn" @click="enableUserGpsTracking" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-if="!saving">Save</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Summary Modal -->
    <div v-if="isLogsOpen" class="modal-overlay" @click.self="activityLog">
      <div class="modal-container">
        <div class="modal-header">
          <h2>Active Users Summary</h2>
          <button class="icon-button close-btn" @click="activityLog">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="user-count-cards">
            <div class="count-card">
              <div class="count-card-icon">
                <i class="fa-solid fa-car"></i>
              </div>
              <div class="count-card-content">
                <div class="count-card-number">{{ userCounts.ASM }}</div>
                <div class="count-card-label">ASM</div>
              </div>
            </div>

            <div class="count-card">
              <div class="count-card-icon">
                <i class="fa-solid fa-motorcycle"></i>
              </div>
              <div class="count-card-content">
                <div class="count-card-number">{{ userCounts.SE }}</div>
                <div class="count-card-label">SE</div>
              </div>
            </div>

            <div class="count-card">
              <div class="count-card-icon">
                <i class="fa-solid fa-truck"></i>
              </div>
              <div class="count-card-content">
                <div class="count-card-number">{{ userCounts.DSR }}</div>
                <div class="count-card-label">DSR</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filtering Menu -->
    <div v-if="isShowFiltering" class="filter-dropdown" ref="filterMenu">
      <ul class="filter-list">
        <li @click="toggleFilter('ASM')">
          <i :class="isShowAsm ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          {{ isShowAsm ? 'Hide ASM' : 'Show ASM' }}
        </li>
        <li @click="toggleFilter('DSR')">
          <i :class="isShowDsr ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          {{ isShowDsr ? 'Hide DSR' : 'Show DSR' }}
        </li>
        <li @click="toggleFilter('SE')">
          <i :class="isShowSe ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          {{ isShowSe ? 'Hide SE' : 'Show SE' }}
        </li>
      </ul>
    </div>

    <!-- Active User Locations Log -->
    <div class="bottom-sheet-overlay" :class="{ open: isLogSheetOpen }" @click.self="toggleLogSheet">
      <div class="bottom-sheet" :class="{ open: isLogSheetOpen }">
        <div class="sheet-header">
          <h2>Active Users Log</h2>
          <button class="icon-button close-btn" @click="toggleLogSheet">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="sheet-body" ref="logContainer">
          <div v-if="userCounts.ASM === 0 && userCounts.SE === 0 && userCounts.DSR === 0" class="empty-logs">
            Waiting for socket connections...
          </div>
          <div v-else class="categorized-logs">
          <div class="log-category" v-if="activeUsersList.DSR.length > 0">
              <h3>DSR List <span class="badge">{{ activeUsersList.DSR.length }}</span></h3>
              <ul class="log-list">
                <li v-for="user in activeUsersList.DSR" :key="user.id" class="log-item success">
                  <span class="log-time">[{{ user.time }}]</span>
                  <span class="log-message">📍 Updated location of <strong>{{ user.name }}</strong> - ({{ user.lat }}, {{ user.lng }})</span>
                </li>
              </ul>
            </div>

            <div class="log-category" v-if="activeUsersList.ASM.length > 0">
              <h3>ASM List <span class="badge">{{ activeUsersList.ASM.length }}</span></h3>
              <ul class="log-list">
                <li v-for="user in activeUsersList.ASM" :key="user.id" class="log-item success">
                  <span class="log-time">[{{ user.time }}]</span>
                  <span class="log-message">📍 Updated location of <strong>{{ user.name }}</strong> - ({{ user.lat }}, {{ user.lng }})</span>
                </li>
              </ul>
            </div>

            <div class="log-category" v-if="activeUsersList.SE.length > 0">
              <h3>SE List <span class="badge">{{ activeUsersList.SE.length }}</span></h3>
              <ul class="log-list">
                <li v-for="user in activeUsersList.SE" :key="user.id" class="log-item success">
                  <span class="log-time">[{{ user.time }}]</span>
                  <span class="log-message">📍 Updated location of <strong>{{ user.name }}</strong> - ({{ user.lat }}, {{ user.lng }})</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Location History Modal Intergation -->
    <LocationHistoryModal 
      v-if="isLocationHistoryOpen" 
      @close-modal="closeLocationHistory" 
    />
  </div>
</template>

<script>
import { initEcho, disconnectEcho } from "@/services/echoService";
import { getGPSTrackingSchedule, saveGPSTrackingSchedule } from "@/services/dataService"
import { useToast } from "vue-toastification";
import LocationHistoryModal from "@/pages/Modal/maps/locationHistoryModal.vue";
export default {
  name: "RealTimeTracker",
  components: {
    LocationHistoryModal
  },
  data() {
    return {
      token: localStorage.getItem('token_set'),
      profileId: localStorage.getItem('profile_id'),
      userCategory: localStorage.getItem('user_label'),
      allowedUserIds: [],
      loading: false,
      saving: false,
      isMenuOpen: false,
      isLogsOpen: false,
      connectionStatus: 'disconnected',
      logs: [], 
      logCount: 0,
      expandedRoles: [],
      daysOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      trackingSchedule: {},
      map: null,
      markers: {},
      hoveredUser: null,
      tooltipStyle: {},
      userCounts: {
        ASM: 0,
        SE: 0,
        DSR: 0
      },
      isFullScreen: false,
      isShowFiltering: false,
      isShowAsm: true,  
      isShowDsr: true,
      isShowSe: true,
      isLogSheetOpen: false,
      activeUsersList: {
        ASM: [],
        SE: [],
        DSR: []
      },
      isLocationHistoryOpen: false,
    };
  },
  computed: {
    connectionStatusClass() {
      return this.connectionStatus === 'connected' ? 'connected' : 'disconnected';
    },
    connectionStatusText() {
      return this.connectionStatus === 'connected' ? 'Connected' : 'Disconnected';
    }
  },
  mounted() {
    const storedIds = localStorage.getItem('UserIds');
    if (storedIds) {
      try {
        // Parse the JSON array: [{"user_id": 295, "description": "SALES REP"}]
        this.allowedUserIds = JSON.parse(storedIds);
        this.addLog(`🔒 Filtering active for ${this.allowedUserIds.length} users`, '');
      } catch (error) {
        console.error("Error parsing UserIds from local storage:", error);
        this.allowedUserIds = [];
      }
    }

    this.loadGoogleMaps();
    window.initMap = this.initMap;
    this.addLog('🔄 Component mounted, initializing WebSocket...', '');

    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    if (window.Echo) {
      window.Echo.leave('locations');
      disconnectEcho();
    }
    // Clean up the listener to prevent memory leaks
    document.removeEventListener('click', this.handleClickOutside);
  },
  setup() {
    const toast = useToast();
    return {
      toast,
    };
  },
  methods: {
    // Automatically enable all days (excluding Sunday) when tracking is enabled
    onTrackingEnabledChange(user) {
      if (user.trackingEnabled) {
        for (const day in user.schedule) {
          if (day !== 'Sunday') {
            user.schedule[day].enabled = true;
          }
        }
      }
    },

    // Navigate back to dashboard
    navigateToDashboard() {
      if(this.isFullScreen === true){
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        this.isFullScreen = false;
        this.$router.push("/");
      } else {
        this.$router.push("/");
      }
    },

    // View Web Page as Full Screen
    fullScreen() {
      const elem = document.documentElement;
      if (!document.fullscreenElement) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.mozRequestFullScreen) {
          elem.mozRequestFullScreen();
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen();
        }
        this.isFullScreen = true;
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        this.isFullScreen = false;
      }
    },

    // Toggle Activity Log Modal
    activityLog() {
      this.isLogsOpen = !this.isLogsOpen;
    },

    // Filtering by User Type (DSR/ASM/SE)
    filteringMap() {
      this.isShowFiltering = !this.isShowFiltering;
    },

    // Toggle Filters by Role
    toggleFilter(role) {
      if (role === 'ASM') this.isShowAsm = !this.isShowAsm;
      if (role === 'DSR') this.isShowDsr = !this.isShowDsr;
      if (role === 'SE') this.isShowSe = !this.isShowSe;
      
      this.applyMapFilters();
    },

    // Apply Selected Filters
    applyMapFilters() {
      Object.values(this.markers).forEach(marker => {
        const userId = String(marker.locationData.user_id);

        // STRICT CHECK: Ensure user is allowed
        const allowedUser = this.allowedUserIds.find(u => String(u.user_id) === userId);
        
        // If not allowed, force hidden and skip
        if (!allowedUser) {
          marker.setVisible(false);
          return;
        }

        // Apply role description strictly from local storage
        const userType = allowedUser.description;

        let isVisible = true;
        if (userType === 'ASM' && !this.isShowAsm) isVisible = false;
        if (userType === 'SALES REP' && !this.isShowDsr) isVisible = false;
        if (userType === 'SALES EXECUTIVE' && !this.isShowSe) isVisible = false;

        marker.setVisible(isVisible);
      });

      this.updateUserCounts(); 
    },

    // Open/Close User's GPS tracking schedule menu
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      if (this.isMenuOpen) {
        this.fetchTrackingSchedule();
      }
    },

    // Expand/Collapse Role Sections
    toggleRoleExpand(role) {
      const index = this.expandedRoles.indexOf(role);
      if (index === -1) this.expandedRoles.push(role);
      else this.expandedRoles.splice(index, 1);
    },

    // Internal Logger (Console only now)
    addLog(message, type = '', data = null) {
      const time = new Date().toLocaleTimeString();
      // We still update the array for logic, but it isn't shown in UI
      this.logs.unshift({
        time,
        message,
        type,
        data: data ? JSON.stringify(data, null, 2) : null
      });
      if (this.logs.length > 50) this.logs.pop();
      // Console log for developer debugging
      console.log(`[${time}] ${message}`, data || '');
    },

    // Load Google Maps Script
    loadGoogleMaps() {
      if (window.google && window.google.maps) {
        this.initMap();
        this.updateUserCounts();
      } else {
        const script = document.createElement("script");
        const googleKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&callback=initMap`;
        script.defer = true;
        script.async = true;
        script.onload = () => this.initMap();
        document.head.appendChild(script);
      }
    },

    // Initialize Google Map
    initMap() {
      this.map = new google.maps.Map(document.getElementById("map"), {
        center: { lat: 7.8731, lng: 80.7718 },
        zoom: 7
      });
      this.addLog('🗺️ Google Maps initialized', 'success');
      this.listenForLocationUpdates();

      // Close the filter menu if the user clicks or drags the map
      this.map.addListener('click', () => {
        if (this.isShowFiltering) this.isShowFiltering = false;
      });
      this.map.addListener('dragstart', () => {
        if (this.isShowFiltering) this.isShowFiltering = false;
      });
    },

    // WebSocket Listener for Location Updates
    listenForLocationUpdates() {
      initEcho();
      if (!window.Echo) {
        console.error("Echo not initialized");
        return;
      }
      const channel = window.Echo.channel('locations');

      channel.on('pusher:subscription_succeeded', () => {
        this.connectionStatus = 'connected';
      });

      channel.on('pusher:subscription_error', (err) => {
        this.connectionStatus = 'disconnected';
        this.addLog('❌ Subscription error', 'error', err);
      });

      channel.listen('.App\\Events\\LocationUpdated', (data) => {
        this.logCount++;
        this.updateMarker(data);
      });

      channel.listen('LocationUpdated', (data) => {
        this.logCount++;
        this.updateMarker(data);
      });
    },

    // Create Custom SVG Icon based on User Type
    createCustomIcon(userType) {
      let svgIcon = '';
      let iconColor = '#000000';
      const iconWidth = 40;
      const iconHeight = 40;

      if (userType === 'ASM') {
        svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="${iconColor}" d="M199.2 181.4L173.1 256L466.9 256L440.8 181.4C436.3 168.6 424.2 160 410.6 160L229.4 160C215.8 160 203.7 168.6 199.2 181.4zM103.6 260.8L138.8 160.3C152.3 121.8 188.6 96 229.4 96L410.6 96C451.4 96 487.7 121.8 501.2 160.3L536.4 260.8C559.6 270.4 576 293.3 576 320L576 512C576 529.7 561.7 544 544 544L512 544C494.3 544 480 529.7 480 512L480 480L160 480L160 512C160 529.7 145.7 544 128 544L96 544C78.3 544 64 529.7 64 512L64 320C64 293.3 80.4 270.4 103.6 260.8zM192 368C192 350.3 177.7 336 160 336C142.3 336 128 350.3 128 368C128 385.7 142.3 400 160 400C177.7 400 192 385.7 192 368zM480 400C497.7 400 512 385.7 512 368C512 350.3 497.7 336 480 336C462.3 336 448 350.3 448 368C448 385.7 462.3 400 480 400z"/></svg>`;
      } else if (userType === 'SALES REP') {
        svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="${iconColor}" d="M32 160C32 124.7 60.7 96 96 96L384 96C419.3 96 448 124.7 448 160L448 192L498.7 192C515.7 192 532 198.7 544 210.7L589.3 256C601.3 268 608 284.3 608 301.3L608 448C608 483.3 579.3 512 544 512L540.7 512C530.3 548.9 496.3 576 456 576C415.7 576 381.8 548.9 371.3 512L268.7 512C258.3 548.9 224.3 576 184 576C143.7 576 109.8 548.9 99.3 512L96 512C60.7 512 32 483.3 32 448L32 160zM544 352L544 301.3L498.7 256L448 256L448 352L544 352zM224 488C224 465.9 206.1 448 184 448C161.9 448 144 465.9 144 488C144 510.1 161.9 528 184 528C206.1 528 224 510.1 224 488zM456 528C478.1 528 496 510.1 496 488C496 465.9 478.1 448 456 448C433.9 448 416 465.9 416 488C416 510.1 433.9 528 456 528z"/></svg>`;
      } else if (userType === 'SALES EXECUTIVE') {
        svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="${iconColor}" d="M280 80C266.7 80 256 90.7 256 104C256 117.3 266.7 128 280 128L336.6 128L359.1 176.7L264 248C230.6 222.9 189 208 144 208L88 208C74.7 208 64 218.7 64 232C64 245.3 74.7 256 88 256L144 256C222.5 256 287.2 315.6 295.2 392L269.8 392C258.6 332.8 206.5 288 144 288C73.3 288 16 345.3 16 416C16 486.7 73.3 544 144 544C206.5 544 258.5 499.2 269.8 440L320 440C333.3 440 344 429.3 344 416L344 393.5C344 348.4 369.7 308.1 409.5 285.8L421.6 311.9C389.2 335.1 368.1 373.1 368.1 416C368.1 486.7 425.4 544 496.1 544C566.8 544 624.1 486.7 624.1 416C624.1 345.3 566.8 288 496.1 288C485.4 288 475.1 289.3 465.2 291.8L433.8 224L488 224C501.3 224 512 213.3 512 200L512 152C512 138.7 501.3 128 488 128L434.7 128C427.8 128 421 130.2 415.5 134.4L398.4 147.2L373.8 93.9C369.9 85.4 361.4 80 352 80L280 80zM445.8 364.4L474.2 426C479.8 438 494 443.3 506 437.7C518 432.1 523.3 417.9 517.7 405.9L489.2 344.3C491.4 344.1 493.6 344 495.9 344C535.7 344 567.9 376.2 567.9 416C567.9 455.8 535.7 488 495.9 488C456.1 488 423.9 455.8 423.9 416C423.9 395.8 432.2 377.5 445.7 364.4zM144 488C104.2 488 72 455.8 72 416C72 376.2 104.2 344 144 344C175.3 344 202 364 211.9 392L144 392C130.7 392 120 402.7 120 416C120 429.3 130.7 440 144 440L211.9 440C202 468 175.3 488 144 488z"/></svg>`;
      } else {
        svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path fill="${iconColor}" d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z"/></svg>`;
      }

      return {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgIcon),
        scaledSize: new google.maps.Size(iconWidth, iconHeight),
        anchor: new google.maps.Point(iconWidth / 2, iconHeight),
      };
    },

    // Update User Counts and Categorized Lists
    updateUserCounts() {
      const counts = { ASM: 0, SE: 0, DSR: 0 };
      const lists = { ASM: [], SE: [], DSR: [] };
      const uniqueUsers = new Set();
      
      Object.values(this.markers).forEach(marker => {
        if (marker.locationData && marker.locationData.user_id) {
          const userId = String(marker.locationData.user_id);
          
          // STRICT CHECK: Skip counting this user if they are not in the allowed list
          const allowedUser = this.allowedUserIds.find(u => String(u.user_id) === userId);
          if (!allowedUser) return; 

          if (!uniqueUsers.has(userId)) {
            uniqueUsers.add(userId);
            
            // Apply role description strictly from local storage
            const userType = allowedUser.description;

            // Create a user object for the log sheet
            const userInfo = {
              id: userId,
              name: marker.locationData.full_name || `User ${userId}`,
              time: marker.lastUpdated || new Date().toLocaleTimeString(),
              lat: parseFloat(marker.locationData.latitude).toFixed(5), 
              lng: parseFloat(marker.locationData.longitude).toFixed(5)
            };

            // Increment count and add to corresponding list based on Local Storage Description
            if (userType === 'ASM') {
              counts.ASM++;
              lists.ASM.push(userInfo);
            } else if (userType === 'SALES EXECUTIVE') {
              counts.SE++;
              lists.SE.push(userInfo);
            } else if (userType === 'SALES REP') {
              counts.DSR++;
              lists.DSR.push(userInfo);
            }
          }
        }
      });
      
      this.userCounts = counts;
      this.activeUsersList = lists; // Update the UI arrays
    },

    // Update or Create Marker on Map
    updateMarker(data) {
      const userId = String(data.user_id);

      // 1. STRICT GATEKEEPER: Find user in the allowed list from local storage
      const allowedUser = this.allowedUserIds.find(u => String(u.user_id) === userId);
      
      // If the user is NOT in the list, ignore them and remove their marker if it exists
      if (!allowedUser) {
        if (this.markers[userId]) {
          this.markers[userId].setMap(null); // Remove from map entirely
          delete this.markers[userId];       // Delete from tracked objects
        }
        return; // Stop processing instantly
      }

      // 2. Use the user type EXACTLY as it is defined in local storage
      const userType = allowedUser.description;
      
      // 3. Check Visibility based on the current toggle states
      let isVisible = true;
      if (userType === 'ASM' && !this.isShowAsm) isVisible = false;
      if (userType === 'SALES REP' && !this.isShowDsr) isVisible = false;
      if (userType === 'SALES EXECUTIVE' && !this.isShowSe) isVisible = false;

      const userName = data.full_name;
      const time = new Date().toLocaleTimeString();

      const pos = {
        lat: parseFloat(data.latitude),
        lng: parseFloat(data.longitude),
      };

      const contentString = `
        <div>
          <span style="font-weight: 600">${data.full_name || 'User'}</span><br>
          <small style="font-weight: 400"><i class="fa-solid fa-phone" style="color: green;"></i> ${data.moblie || data.mobile || 'N/A'}</small><br>
          <small style="font-weight: 400"><i class="fa-solid fa-clock" style="color: grey;"></i> ${time}</small>
        </div>
      `;

      if (this.markers[userId]) {
        // --- EXISTING MARKER UPDATE ---
        this.markers[userId].setPosition(pos);
        this.markers[userId].locationData = data;
        this.markers[userId].lastUpdated = time; 
        
        // Apply visibility state
        this.markers[userId].setVisible(isVisible);

        if (this.markers[userId].infoWindow) {
          this.markers[userId].infoWindow.setContent(contentString);
        }

        this.addLog(`📍 Updated location of ${userName}`, '');
      } else {
        // --- NEW MARKER CREATION ---
        const marker = new google.maps.Marker({
          position: pos,
          map: this.map,
          draggable: false,
          title: `(${data.latitude}, ${data.longitude})`,
          icon: this.createCustomIcon(userType),
          visible: isVisible // Apply visibility on creation
        });

        marker.locationData = data;
        marker.lastUpdated = time;

        marker.infoWindow = new google.maps.InfoWindow({
          disableAutoPan: true,
          content: contentString
        });

        marker.addListener('mouseover', () => {
          marker.infoWindow.open(this.map, marker);
          setTimeout(() => {
            const closeButton = document.querySelectorAll('.gm-ui-hover-effect')[0];
            if (closeButton) closeButton.style.display = 'none';
          }, 10);
        });

        marker.addListener('mouseout', () => {
          marker.infoWindow.close();
        });

        this.markers[userId] = marker;
        this.addLog(`✨ Created new marker for User ${userId}`, 'success');
      }
      this.updateUserCounts();
    },

    // Convert API Response to Tracking Schedule Format
    mapApiResponseToTrackingSchedule(apiData) {
      const trackingSchedule = {};
      for (const [role, users] of Object.entries(apiData)) {
        const displayRole = this.formatRoleName(role);
        trackingSchedule[displayRole] = users.map(user => {
          const schedule = {};
          this.daysOfWeek.forEach(day => {
            const dayData = user.schedule[day] || { is_active: false, start_time: null, end_time: null };
            schedule[day] = {
              enabled: dayData.is_active || false,
              start: dayData.start_time || '08:30',
              end: dayData.end_time || '17:00'
            };
          });

          return {
            id: user.user_id,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User ${user.user_id}`,
            trackingEnabled: user.is_geotracker === 1,
            schedule: schedule
          };
        });
      }
      return trackingSchedule;
    },

    // Format Role Names for Display
    formatRoleName(apiRoleName) {
      const roleMappings = {
        'SALES REP': 'Distributor Sales Rep (DSR)',
        'SALES EXECUTIVE': 'Sales Executives (SE)',
        'ASM': 'Area Sales Managers (ASM)'
      };
      return roleMappings[apiRoleName] || apiRoleName;
    },

    // Fetch Tracking Schedule from API
    async fetchTrackingSchedule() {
      try {
        this.loading = true;
        const response = await getGPSTrackingSchedule(this.token, this.profileId);
        if (response.data.success && response.data.data) {
          this.trackingSchedule = this.mapApiResponseToTrackingSchedule(response.data.data);
          this.expandedRoles = Object.keys(this.trackingSchedule);
        } else {
          this.trackingSchedule = {};
        }
      } catch (e) {
        console.error(e);
        this.trackingSchedule = {};
      } finally {
        this.loading = false;
      }
    },

    // Save GPS Tracking Schedule to API
    async enableUserGpsTracking() {
      try {
        this.saving = true;
        const validationErrors = this.validateTrackingSchedule();
        if (validationErrors.length > 0) {
          validationErrors.forEach(error => {
            this.toast.error(error);
          });
          return;
        }

        const payload = {
          created_by: parseInt(this.profileId),
          users: []
        };

        for (const role in this.trackingSchedule) {
          this.trackingSchedule[role].forEach((user) => {
            const userPayload = {
              assign_user_id: user.id,
              is_geotracker: user.trackingEnabled ? 1 : 0,
              schedules: [],
            };

            this.daysOfWeek.forEach(day => {
              const daySchedule = user.schedule[day];
              const startTime = daySchedule.enabled && daySchedule.start
                  ? this.formatTimeToHHMMSS(daySchedule.start)
                  : "00:00:00";
              const endTime = daySchedule.enabled && daySchedule.end
                  ? this.formatTimeToHHMMSS(daySchedule.end)
                  : "00:00:00";

              const scheduleEntry = {
                day_name: day,
                start_time: startTime,
                end_time: endTime,
                is_active: daySchedule.enabled
              };
              userPayload.schedules.push(scheduleEntry);
            });
            payload.users.push(userPayload);
          });
        }
        const response = await saveGPSTrackingSchedule(this.token, payload);
        if(response.data.success === true) {
          this.toast.success(response.data.message);
          this.isMenuOpen = false;
        } else {
          this.toast.error(response.data.message || "Failed to save GPS tracking settings. Please try again.");
        }
      } catch (e) {
        this.toast.error("Failed to save GPS tracking settings. Please try again.");
      } finally {
        this.saving = false;
      }
    },

    // Validate Tracking Schedule before Saving
    validateTrackingSchedule() {
      const errors = [];
      for (const role in this.trackingSchedule) {
        this.trackingSchedule[role].forEach((user) => {
          if (user.trackingEnabled) {
            let hasEnabledDay = false;
            for (const day in user.schedule) {
              if (user.schedule[day].enabled) {
                hasEnabledDay = true;
                const daySchedule = user.schedule[day];
                if (!daySchedule.start || !daySchedule.end) {
                  errors.push(`User "${user.name}" has enabled ${day} but missing start or end time`);
                } else if (daySchedule.start === "00:00:00" && daySchedule.end === "00:00:00") {
                  errors.push(`User "${user.name}" has enabled ${day} but times are not set`);
                } else if (this.isInvalidTime(daySchedule.start) || this.isInvalidTime(daySchedule.end)) {
                  errors.push(`User "${user.name}" has invalid time format for ${day}`);
                } else if (this.isEndTimeBeforeStartTime(daySchedule.start, daySchedule.end)) {
                  errors.push(`User "${user.name}" has end time before start time for ${day}`);
                }
              }
            }
            if (!hasEnabledDay) {
              errors.push(`User "${user.name}" has tracking enabled but no days are selected.`);
            }
          }
        });
      }
      return errors;
    },

    // Check if Time String is Valid
    isInvalidTime(timeString) {
      if (!timeString) return true;
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
      return !timeRegex.test(timeString);
    },

    // Check if End Time is Before Start Time
    isEndTimeBeforeStartTime(startTime, endTime) {
      const start = this.timeToMinutes(startTime);
      const end = this.timeToMinutes(endTime);
      return end <= start;
    },

    // Convert HH:MM Time String to Minutes
    timeToMinutes(timeString) {
      if (!timeString) return 0;
      const parts = timeString.split(':');
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      return hours * 60 + minutes;
    },

    // Format Time to HH:MM:SS
    formatTimeToHHMMSS(timeString) {
      if (!timeString || timeString === '00:00:00') return "00:00:00";
      if (timeString.includes(':')) {
        const parts = timeString.split(':');
        if (parts.length >= 2) {
          return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
        }
      }
      return "00:00:00";
    },

    // Method to detect clicks outside the button and menu
    handleClickOutside(event) {
      const filterBtn = this.$refs.filterBtn;
      const filterMenu = this.$refs.filterMenu;

      // If the menu is open, check if the click target is outside both the button and the menu
      if (this.isShowFiltering && filterBtn && filterMenu) {
        if (!filterBtn.contains(event.target) && !filterMenu.contains(event.target)) {
          this.isShowFiltering = false;
        }
      }
    },

    // Toggle Log Bottom Sheet
    toggleLogSheet() {
      this.isLogSheetOpen = !this.isLogSheetOpen;
    },

    // Show Location History Modal
    showLocationHistory() {
      this.isLocationHistoryOpen = true;
      this.isFullScreen = true;
    },

    // Close Location History Modal
    closeLocationHistory() {
      this.isLocationHistoryOpen = false;
    }
  },
};
</script>

<style scoped>
/* Layout & Structure */
.app-container {
  min-height: 100vh;
}
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  z-index: 1000;
  padding: 0 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 64px;
  width: 100%;
}
.title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  text-align: left;
  margin-right: auto;
}
.main-content {
  position: absolute;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
}
.map-container {
  width: 100%;
  height: 100%;
}

/* Buttons */
.icon-button {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #EEEEEE;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  color: #666;
}
.icon-button:hover {
  background: rgba(0, 0, 0, 0.1);
}
.icon-button:active {
  background: rgba(0, 0, 0, 0.2);
}
.close-btn {
  width: 36px;
  height: 36px;
}
.tooltip-text {
  visibility: hidden;
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 6px 10px;
  border-radius: 4px;
  position: absolute;
  top: 110%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 10px;
  font-family: Google Sans, sans-serif;
  font-weight: 500;
  z-index: 1001;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}
.back-btn:hover .tooltip-text,
.expand-btn:hover .tooltip-text,
.activity-btn:hover .tooltip-text,
.filter-btn:hover .tooltip-text,
.menu-btn:hover .tooltip-text,
.log-btn:hover .tooltip-text,
.close-btn:hover .tooltip-text,
.history-btn:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
.save-btn {
  width: 100%;
  padding: 10px 18px;
  border: none;
  background: #4caf50;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.save-btn:hover {
  background: #45a049;
}
.save-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

/* Connection Status */
.connection-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
}
.connection-status.connected {
  background-color: #d4edda;
  color: #155724;
}
.connection-status.disconnected {
  background-color: #f8d7da;
  color: #721c24;
}
.connection-status .status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: pulse 2s infinite;
}
.connection-status.connected .status-dot {
  background-color: #28a745;
}
.connection-status.disconnected .status-dot {
  background-color: #dc3545;
}

/* Sidebar Overlay Menu (For Settings) */
.overlay-menu {
  position: fixed;
  top: 0;
  right: -500px;
  width: 450px;
  height: 100%;
  background: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  transition: right 0.3s ease;
  overflow: hidden;
}
.overlay-menu.open {
  right: 0;
}
.menu-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
}
.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
}
.menu-header h2 {
  font-size: 18px;
  margin: 0;
}
.menu-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
}
.menu-footer {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  background: white;
  position: sticky;
  bottom: 0;
  z-index: 10;
  flex-shrink: 0;
}

/* --- MODAL STYLES (For Counts) --- */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}
.modal-container {
  background: white;
  width: 90%;
  max-width: 600px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  overflow: hidden;
  animation: slideDown 0.3s ease-out;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
  background: #f9f9f9;
}
.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}
.modal-body {
  padding: 24px;
}
@keyframes slideDown {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* User Count Cards (Adapted for Modal) */
.user-count-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.count-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s;
}
.count-card:hover {
  transform: translateY(-2px);
}
.count-card-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: #000;
  color: white;
}
.count-card-content {
  text-align: center;
}
.count-card-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 4px;
  color: #333;
}
.count-card-label {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Loading States */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
}
.location-loader {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.location-loader i {
  font-size: 48px;
  color: #4caf50;
  animation: bounce 1.5s infinite ease-in-out;
}
.location-loader .pulse {
  position: absolute;
  width: 80px;
  height: 80px;
  border: 2px solid #4caf50;
  border-radius: 50%;
  animation: pulseRing 1.5s infinite ease-out;
}

/* Role & User Cards */
.role-card {
  background: #f9f9f9;
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.role-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.role-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.user-card {
  background: #fff;
  padding: 12px;
  margin: 8px 0;
  border-radius: 6px;
  border: 1px solid #ddd;
}
.user-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.user-name {
  font-weight: 500;
  font-size: 15px;
}

/* Toggle Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  background-color: #f44336;
  transition: 0.4s;
  border-radius: 34px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background: #fff;
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: #4caf50;
}
input:checked + .slider:before {
  transform: translateX(20px);
}

/* Day Schedule Grid */
.day-schedule {
  display: grid;
  grid-template-columns: 30px 55px 1fr 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.day-checkbox {
  display: flex;
  justify-content: center;
}
.day-label {
  width: 50px;
  font-weight: 600;
  font-size: 14px;
}
.day-schedule input[type="time"] {
  width: 100%;
  padding: 5px;
  border-radius: 4px;
  font-size: 13px;
  border: 1px solid #ccc;
}

/* Error States */
.not-found-widget {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
}
.not-found-widget img {
  width: 250px;
  max-width: 100%;
}
.not-found-widget p {
  color: grey;
  font-size: 18px;
  font-weight: bold;
  margin-top: -10px;
  text-align: center;
}

/* Filtering Dropdown Styles */
.filter-dropdown {
  position: absolute;
  top: 70px;
  right: 120px; /* Adjust this to align exactly under your filter button */
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2500;
  border: 1px solid #eee;
  min-width: 160px;
  overflow: hidden;
}
.filter-list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}
.filter-list li {
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.2s ease;
}
.filter-list li i {
  color: #666;
  width: 16px;
}
.filter-list li:hover {
  background-color: #f5f5f5;
  color: #000;
}

/* Bottom Sheet for View User Logs */
.bottom-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 4000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}
.bottom-sheet-overlay.open {
  opacity: 1;
  visibility: visible;
}
.bottom-sheet {
  position: absolute;
  bottom: -100%;
  left: 0;
  width: 100%;
  height: 45vh; /* Takes up bottom 45% of the screen */
  background: #fff;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  transition: bottom 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.bottom-sheet.open {
  bottom: 0;
}
.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f9f9f9;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-bottom: 1px solid #ddd;
}
.sheet-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}
.sheet-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  background: #1e1e1e; /* Terminal Dark Theme */
  color: #d4d4d4;
}
.empty-logs {
  color: #888;
  text-align: center;
  margin-top: 30px;
  font-style: italic;
}
.log-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.log-item {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #333;
  word-wrap: break-word;
}
.log-time {
  color: #569cd6; /* VS Code blue */
  margin-right: 8px;
  font-weight: bold;
}
.log-message {
  color: #ce9178; /* VS Code string color */
}
.log-item.success .log-message {
  color: #4caf50;
}
.log-item.error .log-message {
  color: #f44336;
}
.log-data {
  margin-top: 4px;
  padding: 8px;
  background: #000;
  border-radius: 4px;
  color: #9cdcfe;
  white-space: pre-wrap;
}
.log-category {
  margin-bottom: 24px;
}
.log-category h3 {
  color: #fff;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #444;
  display: flex;
  align-items: center;
  gap: 10px;
}
.badge {
  background: #4caf50;
  color: #000;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: bold;
}
.log-message strong {
  color: #fff; /* Highlights the user's name */
}

/* Animations */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
@keyframes pulseRing {
  0% {
    transform: scale(0.6);
    opacity: 0.8;
  }
  70% {
    transform: scale(1.4);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

/* Responsive Design */
@media (max-width: 480px) {
  .header {
    padding: 0 12px;
  }

  .title {
    font-size: 16px;
  }

  .icon-button {
    width: 36px;
    height: 36px;
  }

  .connection-status {
    font-size: 11px;
    padding: 6px 10px;
  }

  .connection-status span {
    display: none;
  }

  .connection-status .status-dot {
    width: 12px;
    height: 12px;
  }

  .overlay-menu {
    width: 80%;
  }

  .main-content {
    top: 56px;
  }

  .day-schedule {
    grid-template-columns: 25px 45px 1fr 1fr;
  }

  /* Modal Responsive */
  .user-count-cards {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>